import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Define our own Params type since react-router-dom doesn't export one
export type Params = Record<string, string | undefined>;

export interface RouterHookWrapperProps {
  history: {
    push: (path: string) => void;
    replace: (path: string) => void;
    go: (delta: number) => void;
    goBack: () => void;
    goForward: () => void;
  };
  location: Location;
  match: {
    params: any;
    path: string;
    url: string;
  };
  params: any; // this is here to catch places that have not been updated
}

const createHistoryObject = (navigate: any) => ({
  push: (path: string) => navigate(path),
  replace: (path: string) => navigate(path, { replace: true }),
  go: (delta: number) => navigate(delta),
  goBack: () => navigate(-1),
  goForward: () => navigate(1),
});

// HEADS UP: This gives you the actual URL path, not the route pattern!
// Components that need the pattern should use useMatch() with the pattern:
//   `const match = useMatch('/users/:id')` -- gives you the pattern
//   NOT `useMatch('/users/123')` -- that's just the current URL
const createMatchObject = (location: Location, params: any) => ({
  params,
  path: location.pathname,
  url: location.pathname,
});

/**
 * Higher-order component that provides React Router props to class components.
 * This HOC is only necessary when using class components that need access to router props.
 * For functional components, use React Router hooks directly (useNavigate, useLocation, useParams, etc.).
 *
 * Note: Consider migrating to functional components with React Router hooks for better performance and maintainability.
 * Note 2: This will work also when wrapping functional components which was done in the upgrade to router v6.
 *
 * @template P - The props type of the wrapped component
 * @param WrappedComponent - The class component to wrap and provide router props to
 * @returns A functional component that passes router props to the wrapped component
 * @warning You really should not be adding this to new components since new components should be functional and use hooks directly.
 *
 * @example
 * // Class component that needs router props
 * class MyClassComponent extends React.Component {
 *   handleNavigation = () => {
 *     this.props.history.push('/new-path');
 *   }
 *
 *   render() {
 *     return <div>Current path: {this.props.location.pathname}</div>;
 *   }
 * }
 *
 * // Wrap with HOC to provide router props
 * export default withRouterHooks(MyClassComponent);
 *
 * @example
 * // For functional components, use hooks directly instead:
 * const MyFunctionalComponent = () => {
 *   const navigate = useNavigate();
 *   const location = useLocation();
 *   const params = useParams();
 *
 *   return <div>Current path: {location.pathname}</div>;
 * };
 */
export function withRouterHooks<T extends Record<string, any> = Params>(Component: React.ComponentType<any>) {
  const WithRouterHooksComponent = (props: any) => {
    const params = useParams() as T;
    const location = useLocation();
    const navigate = useNavigate();

    const history = createHistoryObject(navigate);

    const match = createMatchObject(location, params);

    return <Component {...props} params={params} location={location} history={history} match={match} />;
  };

  WithRouterHooksComponent.displayName = `withRouterHooks(${Component.displayName || Component.name})`;

  return WithRouterHooksComponent;
}

/**
 * Render prop component that provides router props to its children via a function.
 * This is an alternative to the withRouterHooks HOC when you need more flexibility
 * in how router props are passed to components. You don't need this when using functional
 * components.
 *
 * Note: Consider migrating to functional components with React Router hooks for better performance and maintainability.
 *
 * @example
 * // Basic usage with render prop
 * <WithRouterHookComponent>
 *   {(routerProps) => (
 *     <MyComponent {...routerProps} />
 *   )}
 * </WithRouterHookComponent>
 *
 * @param children - Function that receives router props and returns React content
 * @returns React component that calls children function with router props
 */
export const WithRouterHookComponent: React.FC<{
  children: (routerProps: RouterHookWrapperProps) => React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  // backward-compatible history object for existing class components
  const history = createHistoryObject(navigate);

  // match object for backward compatibility
  const match = createMatchObject(location, params);

  const routerProps: RouterHookWrapperProps = {
    history,
    location,
    match: {
      ...match,
      params,
    },
    params,
  };

  return <>{children(routerProps)}</>;
};

/**
 * Typed version of withRouterHooks that provides type safety for route parameters.
 *
 * This function allows you to specify the exact shape of your route parameters,
 * giving you TypeScript intellisense and compile-time type checking when accessing
 * params in your components.
 *
 * @template TParams - Interface describing the route parameters for this component
 * @param Component - The component to wrap with typed router props
 * @returns A component with strongly-typed route parameters
 *
 * @example
 * // Define your route parameter interface
 * interface MyRouteParams {
 *   fourfour: string;
 *   revisionSeq: string;
 *   sourceId?: string;
 * }
 *
 * // Your component will receive typed params
 * const MyComponent = ({ params }: { params: MyRouteParams }) => {
 *   // TypeScript knows params.fourfour is a string
 *   // You'll get autocomplete and type checking
 *   return <div>Editing {params.fourfour}</div>;
 * };
 *
 * // Wrap with typed HOC
 * export default withRouterHooksTyped<MyRouteParams>(MyComponent);
 */
export function withRouterHooksTyped<TParams extends Record<string, any>>(
  Component: React.ComponentType<any>,
): React.ComponentType<any> {
  return withRouterHooks<TParams>(Component);
}
